import React, { useCallback, useMemo } from 'react'
import { FinanceDepositWayGroupListItem } from '@golden/gdk-admin'
import { useNavigate } from 'react-router-dom'
import Grid from '@material-ui/core/Grid'
import Button from '@material-ui/core/Button'
import allRoute from '../route/route'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import useT from '../../../i18ns/admin/useT'
import useGDK from '../../../providers/admin/gdk/useGDK'
import useGlobalDialog from '../../../providers/admin/dialog/useGlobalDialog'
import { useDialogHandleClick, useRequestFromSearch } from '../../../utils/default/ComplexFlowHook'
import { createGlobalDialogConfig } from '../../../utils/default/DialogHelper'
import { searchToRequest } from '../../../views/admin/finance/FinanceDepositWayPage'
import useGDKStore from '../../../providers/admin/gdk/useGDKStore'

const ButtonsBase: React.FC<{
  status: boolean
  handleStart: () => void
  handleStop: () => void
  handleDelete: () => void
  handleUpdate: () => void
}> = React.memo((props) => {
  const {
    status,
    handleStart,
    handleStop,
    handleDelete,
    handleUpdate
  } = props
  const classes = useCommonStyles()
  const { t } = useT()
  return (
    <Grid container direction="column" spacing={1} justifyContent="center">
      {
        !status &&
          (
            <Grid item>
              <Button
                onClick={handleStart}
                size="small"
                className={classes.greenGradualOutlineButton}
              >
                {t('common.toggleOn')}
              </Button>
            </Grid>
          )
      }
      {
        status &&
          (
            <Grid item>
              <Button
                onClick={handleStop}
                size="small"
                className={classes.greyOutlineButton}
              >
                {t('common.toggleOff')}
              </Button>
            </Grid>
          )
      }
      <Grid item>
        <Button
          size="small"
          className={classes.blueGradualOutlineButton}
          onClick={handleUpdate}
        >
          {t('common.update')}
        </Button>
      </Grid>
      <Grid item>
        <Button
          onClick={handleDelete}
          size="small"
          className={classes.pinkGradualOutlineButton}
        >
          {t('common.delete')}
        </Button>
      </Grid>
    </Grid>
  )
})

interface PropTypes {
  item: FinanceDepositWayGroupListItem
  reload: () => void
}

const FinanceDepositWayButtons: React.FC<PropTypes> = (props) => {
  const { item, reload } = props
  const { t } = useT()
  const gdk = useGDK()
  const globalDialog = useGlobalDialog()
  const navigate = useNavigate()
  const request = useRequestFromSearch({ searchToRequest })
  const forwarders = useGDKStore.finance.forwarders()

  const startPayload = useMemo(() => ({
    name: item.name,
    is_active: true,
    method: item.method,
    support_accounts: item.support_accounts.map((e) => (e.id)),
    support_ways: item.support_ways.map((e) => (e.id))
  }), [item])
  const handleStart = useDialogHandleClick({
    dialogId: `startDepositWayGroup-${item.id}`,
    globalDialog,
    changeDialogConfig: createGlobalDialogConfig({
      showIcon: false,
      message: t('dialog.confirmToggleOnDepositWayGroup', { name: item.name })
    }),
    successDialogConfig: createGlobalDialogConfig({
      showIcon: false,
      message: t('dialog.toggleOnFinish'),
      showCancel: false
    }),
    getFailDialogConfig: (error: string) => createGlobalDialogConfig({
      variant: 'error',
      showIcon: true,
      message: error,
      showCancel: false
    }),
    payload: startPayload,
    gdkFunc: (payload) => gdk.finance.updateDepositWayGroup(item.id, payload),
    gdkFuncDependencies: [gdk, item.id],
    onSuccess: () => { reload() },
    onSuccessDependencies: [reload]
  })

  const stopPayload = useMemo(() => ({
    name: item.name,
    is_active: false,
    method: item.method,
    support_accounts: item.support_accounts.map((e) => (e.id)),
    support_ways: item.support_ways.map((e) => (e.id))
  }), [item])
  const handleStop = useDialogHandleClick({
    dialogId: `stopDepositWayGroup-${item.id}`,
    globalDialog,
    changeDialogConfig: createGlobalDialogConfig({
      showIcon: false,
      message: t('dialog.confirmToggleOffDepositWayGroup', { name: item.name })
    }),
    successDialogConfig: createGlobalDialogConfig({
      showIcon: false,
      message: t('dialog.toggleOffFinish'),
      showCancel: false
    }),
    getFailDialogConfig: (error: string) => createGlobalDialogConfig({
      variant: 'error',
      showIcon: true,
      message: error,
      showCancel: false
    }),
    payload: stopPayload,
    gdkFunc: (payload) => gdk.finance.updateDepositWayGroup(item.id, payload),
    gdkFuncDependencies: [gdk, item.id],
    onSuccess: () => { reload() },
    onSuccessDependencies: [reload]
  })

  const handleDelete = useDialogHandleClick({
    dialogId: `deleteDepositWay-${item.id}`,
    globalDialog,
    changeDialogConfig: createGlobalDialogConfig({
      showIcon: false,
      message: t('dialog.confirmDeleteDepositWayGroup', { name: item.name })
    }),
    successDialogConfig: createGlobalDialogConfig({
      showIcon: false,
      message: t('dialog.deleteFinish'),
      showCancel: false
    }),
    getFailDialogConfig: (error: string) => createGlobalDialogConfig({
      variant: 'error',
      showIcon: true,
      message: error,
      showCancel: false
    }),
    payload: item.id,
    gdkFunc: (payload) => gdk.finance.deleteDepositWayGroup(payload),
    gdkFuncDependencies: [gdk],
    onSuccess: () => { reload() },
    onSuccessDependencies: [reload]
  })

  const handleUpdate = useCallback(() => {
    navigate(allRoute.financeDepositWayGroupUpdate.encodePath({ search: { ...request }, param: { id: item.id } }))
  }, [globalDialog, item.id, navigate, request, t])

  return (
    <ButtonsBase
      status={item.is_active}
      handleStart={handleStart}
      handleStop={handleStop}
      handleDelete={handleDelete}
      handleUpdate={handleUpdate}
    />
  )
}

export default React.memo(FinanceDepositWayButtons)
